import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import { MODULE_PATHS } from '../../../config/api-paths';
import type { ApplyGlobalRoleTemplateResponse, AssignPermissionRequest, AssignRoleRequest, CreatePermissionRequest, CreateRoleRequest, ListGlobalRoleTemplatesResponse, ListPermissionsResponse, ListRolesResponse, ListTenantUsersResponse, RegisterUserRequest, RegisterUserResponse, RoleModuleAccessResponse, RoleScreenPermissionsResponse, UnassignRoleRequest, UpdateRoleModuleAccessRequest, UpdateRoleScreenPermissionsRequest, UserApiAccessResponse, UserModuleAccessResponse, Uuid } from '../types';

export function bindRbac(client: ApiClient) {
  return {
async listRoles(params?: PaginationQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return client.request<ListRolesResponse>(`/roles${suffix}`);
  },

async listPermissions(params?: PaginationQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return client.request<ListPermissionsResponse>(`/permissions${suffix}`);
  },

async createRole(body: CreateRoleRequest) {
    return client.request<{ role_id: Uuid }>('/roles', { method: 'POST', body: JSON.stringify(body) });
  },

async deleteRole(roleId: Uuid) {
    return client.request<void>(`/roles/${encodeURIComponent(roleId)}`, { method: 'DELETE' });
  },

async createPermission(body: CreatePermissionRequest) {
    return client.request<{ permission_id: Uuid }>('/permissions', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async assignRole(body: AssignRoleRequest) {
    return client.request<void>('/roles/assign', { method: 'POST', body: JSON.stringify(body) });
  },

async unassignRole(body: UnassignRoleRequest) {
    return client.request<void>('/roles/unassign', { method: 'POST', body: JSON.stringify(body) });
  },

async listTenantUsers(params?: PaginationQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return client.request<ListTenantUsersResponse>(`/users${suffix}`);
  },

async registerUser(body: RegisterUserRequest) {
    return client.request<RegisterUserResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async assignPermissionToRole(body: AssignPermissionRequest) {
    return client.request<void>('/roles/permissions/assign', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getRoleScreenPermissions(roleId: Uuid) {
    return client.request<RoleScreenPermissionsResponse>(
      `/roles/${encodeURIComponent(roleId)}/screen-permissions`
    );
  },

async updateRoleScreenPermissions(roleId: Uuid, body: UpdateRoleScreenPermissionsRequest) {
    return client.request<RoleScreenPermissionsResponse>(
      `/roles/${encodeURIComponent(roleId)}/screen-permissions`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      }
    );
  },

    async getRoleModuleAccess(roleId: Uuid) {
      return client.request<RoleModuleAccessResponse>(
        `/roles/${encodeURIComponent(roleId)}/module-access`
      );
    },

    async updateRoleModuleAccess(roleId: Uuid, body: UpdateRoleModuleAccessRequest) {
      return client.request<RoleModuleAccessResponse>(
        `/roles/${encodeURIComponent(roleId)}/module-access`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async getMyModuleAccess() {
      return client.request<UserModuleAccessResponse>(MODULE_PATHS.myModuleAccess);
    },

    /** Per-endpoint API access for the current user, computed from DB role permissions. */
    async getMyApiAccess() {
      return client.request<UserApiAccessResponse>(MODULE_PATHS.myApiAccess);
    },

    async listGlobalRoleTemplates() {
      return client.request<ListGlobalRoleTemplatesResponse>('/role-templates');
    },

    async applyGlobalRoleTemplate(templateName: string) {
      return client.request<ApplyGlobalRoleTemplateResponse>(
        `/role-templates/${encodeURIComponent(templateName)}/apply`,
        { method: 'POST' }
      );
    },
  };
}
